/**
 * AetherLite — Medidas corporales del cliente (histórico).
 *
 * Endpoints (ver `aetherlite_clients.py`, sección "Medidas corporales"):
 *   GET    /aetherlite/clients/:id/measurements              → MeasurementDisplay[] (más reciente primero)
 *   POST   /aetherlite/clients/:id/measurements              → MeasurementDisplay
 *   DELETE /aetherlite/clients/:id/measurements/:mid         → 204
 *
 * Shape espejo exacto de `MeasurementDisplay` del backend: todos los campos
 * numéricos son opcionales; el backend acepta `measured_at` opcional (default
 * server-side = hoy). Al crear una medición con `weight_kg`, el backend además
 * sincroniza `current_weight_kg` del cliente.
 */
import { apiFetch } from "./client";

export interface ClientMeasurement {
  id: number;
  client_id: number;
  measured_at: string; // YYYY-MM-DD
  created_at: string; // ISO
  // Composición
  weight_kg?: number | null;
  height_cm?: number | null;
  body_fat_pct?: number | null;
  // Perímetros (cm)
  neck_cm?: number | null;
  shoulders_cm?: number | null;
  chest_cm?: number | null;
  arm_relaxed_cm?: number | null;
  arm_flexed_cm?: number | null;
  forearm_cm?: number | null;
  waist_cm?: number | null;
  abdomen_cm?: number | null;
  hip_cm?: number | null;
  glute_cm?: number | null;
  thigh_cm?: number | null;
  knee_cm?: number | null;
  calf_cm?: number | null;
  notes?: string | null;
}

/** Payload para POST — mismos campos, todos opcionales (sin id/client_id/created_at). */
export type MeasurementInput = Partial<Omit<ClientMeasurement, "id" | "client_id" | "created_at">>;

/**
 * Lista ordenada de campos de medida con su label en español y unidad.
 * Para iterar en la UI (filas de la tabla de progreso, inputs del modal).
 * El orden sigue: composición → perímetros tren superior → core → tren inferior.
 */
export const MEASUREMENT_FIELDS: { key: keyof MeasurementInput; label: string; unit: string }[] = [
  { key: "weight_kg", label: "Peso", unit: "kg" },
  { key: "height_cm", label: "Altura", unit: "cm" },
  { key: "body_fat_pct", label: "% Grasa", unit: "%" },
  { key: "neck_cm", label: "Cuello", unit: "cm" },
  { key: "shoulders_cm", label: "Espalda / Hombros", unit: "cm" },
  { key: "chest_cm", label: "Pecho", unit: "cm" },
  { key: "arm_relaxed_cm", label: "Brazo relajado", unit: "cm" },
  { key: "arm_flexed_cm", label: "Brazo contraído", unit: "cm" },
  { key: "forearm_cm", label: "Antebrazo", unit: "cm" },
  { key: "waist_cm", label: "Cintura", unit: "cm" },
  { key: "abdomen_cm", label: "Abdomen", unit: "cm" },
  { key: "hip_cm", label: "Cadera", unit: "cm" },
  { key: "glute_cm", label: "Glúteo", unit: "cm" },
  { key: "thigh_cm", label: "Muslo / Pierna", unit: "cm" },
  { key: "knee_cm", label: "Rodilla", unit: "cm" },
  { key: "calf_cm", label: "Pantorrilla", unit: "cm" },
];

/** Histórico de medidas del cliente — más reciente primero (orden del backend). */
export async function listMeasurements(clientId: number): Promise<ClientMeasurement[]> {
  return apiFetch<ClientMeasurement[]>(`/aetherlite/clients/${clientId}/measurements`);
}

/** Registra una nueva toma. Si `measured_at` no viene, el backend usa hoy. */
export async function createMeasurement(
  clientId: number,
  data: MeasurementInput,
): Promise<ClientMeasurement> {
  return apiFetch<ClientMeasurement>(`/aetherlite/clients/${clientId}/measurements`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteMeasurement(clientId: number, measurementId: number): Promise<void> {
  return apiFetch<void>(`/aetherlite/clients/${clientId}/measurements/${measurementId}`, {
    method: "DELETE",
  });
}
